import type { ComponentPublicInstance } from 'vue';

const MAX_NESTED_INSTANCE_DEPTH = 3;

type VueInternalInstance = {
  subTree?: {
    el?: Node;
    component?: {
      proxy?: unknown;
      subTree?: VueInternalInstance['subTree'];
    };
  };
};

function isComponentLike(
  instance: unknown
): instance is ComponentPublicInstance & { $?: VueInternalInstance } {
  return instance != null && typeof instance === 'object' && '$el' in instance;
}

function collectComponentInstanceCandidates(instance: unknown): unknown[] {
  const candidates: unknown[] = [];

  if (!isComponentLike(instance)) {
    return candidates;
  }

  let current: unknown = instance;

  for (let depth = 0; depth < MAX_NESTED_INSTANCE_DEPTH; depth += 1) {
    if (!current || candidates.includes(current)) {
      break;
    }

    candidates.push(current);

    if (!isComponentLike(current)) {
      break;
    }

    const nestedProxy = current.$?.subTree?.component?.proxy;
    if (!nestedProxy || nestedProxy === current) {
      break;
    }

    current = nestedProxy;
  }

  return candidates;
}

function findFirstElementAmongSiblings(anchor: Node): Element | null {
  if (anchor instanceof Element) {
    return anchor;
  }

  let sibling: Node | null = anchor.nextSibling;
  while (sibling) {
    if (sibling instanceof Element) {
      return sibling;
    }
    sibling = sibling.nextSibling;
  }

  sibling = anchor.previousSibling;
  while (sibling) {
    if (sibling instanceof Element) {
      return sibling;
    }
    sibling = sibling.previousSibling;
  }

  return null;
}

/**
 * When fragment sibling walks fail, pick the first direct element child of the
 * anchor parent. Limited to the component fragment container, not document-wide.
 */
function resolveFromFragmentParent(anchor: Node): Element | null {
  const parent = anchor.parentElement;
  if (!parent) {
    return null;
  }

  const directChild = parent.querySelector(':scope > *');
  return directChild instanceof Element ? directChild : null;
}

function resolveFromRootNode(rootNode: Node): Element | null {
  const fromSiblings = findFirstElementAmongSiblings(rootNode);
  if (fromSiblings) {
    return fromSiblings;
  }

  return resolveFromFragmentParent(rootNode);
}

function resolveFromComponentInstance(
  instance: ComponentPublicInstance
): Element | null {
  const rootNode = instance.$el;

  if (!rootNode) {
    return null;
  }

  if (rootNode instanceof Element) {
    return rootNode;
  }

  return resolveFromRootNode(rootNode);
}

/**
 * Resolves the primary DOM element for a Vue component ref (including fragment
 * roots, async component wrappers, and direct element refs).
 */
export function resolveComponentRootElement(instance: unknown): Element | null {
  if (instance instanceof Element) {
    return instance;
  }

  const candidates = collectComponentInstanceCandidates(instance);

  for (const candidate of candidates) {
    if (!isComponentLike(candidate)) {
      continue;
    }

    const resolved = resolveFromComponentInstance(candidate);
    if (resolved) {
      return resolved;
    }
  }

  return null;
}
